// src/clients/components/credito-header.ts
/**
 * Componente compacto de crédito para mostrar en el header
 * Se actualiza automáticamente cuando cambia el carrito
 */
import { LitElement, html, css, unsafeCSS } from "lit";
import { customElement, state } from "lit/decorators.js";
import ApiService from "../../utils/fetch.js";
import globalStyles from "../../global.css?inline";

interface CreditoResponse {
  success: boolean;
  data?: {
    credito_total_api: number;
    total_carrito: number;
    credito_disponible: number;
    tiene_credito_suficiente: boolean;
  };
  message?: string;
}

// ── Singleton de datos compartido entre todas las instancias ──────────────────
// Evita que dos <credito-header> (desktop + mobile offcanvas) hagan fetch doble.
// Solo la instancia que adquiere el "lock" hace el GET; al terminar broadcastea
// el resultado vía el evento interno "credito-data-updated" para que la otra
// instancia se actualice sin tocar la red.
let _fetchLockActive = false;
let _sharedCredito: number | null = null;
let _sharedUpdateTime: Date | null = null;
let _sharedError: string | null = null;

function broadcastCreditoData() {
  window.dispatchEvent(new CustomEvent("credito-data-updated", {
    detail: { credito: _sharedCredito, updateTime: _sharedUpdateTime, error: _sharedError }
  }));
}
// ─────────────────────────────────────────────────────────────────────────────

@customElement("credito-header")
export class CreditoHeader extends LitElement {
  @state() private creditoDisponible: number | null = null;
  @state() private lastKnownCredito: number | null = null;
  @state() private lastUpdateTime: Date | null = null;
  @state() private isLoading = true;
  @state() private hasError = false;
  @state() private errorMessage = "";
  @state() private manualRetryCount = 0;

  private refreshInterval?: number;
  private readonly MAX_MANUAL_RETRIES = 3;
  private themeObserver?: MutationObserver;

  private creditoUpdateTimer: number | null = null;
  private readonly CREDITO_DEBOUNCE_DELAY = 500;
  private readonly CREDITO_COOLDOWN_MS = 8000; // No refrescar si el fetch fue hace menos de 8s

  /* =================== ESTILOS CONSOLIDADOS =================== */
  /* Todos los estilos para credito-header han sido consolidados en */
  /* global.css (Sección 22: ESTILOS CONSOLIDADOS PARA CARRITO-CLIENTE Y CREDITO-HEADER) */
  /* Se inyectan vía unsafeCSS para que el Shadow DOM pueda acceder a ellos */
  static styles = css`${unsafeCSS(globalStyles)}`;

  connectedCallback() {
    super.connectedCallback();
    this.syncTheme();
    this.observeThemeChanges();
    // Si ya hay datos cargados en el singleton, aplicarlos inmediatamente
    if (_sharedCredito !== null || _sharedError !== null) {
      this.applySharedData(_sharedCredito, _sharedUpdateTime, _sharedError);
    } else {
      this.loadCredito();
    }
    this.setupRefreshInterval();
    this.setupEventListeners();
  }

  private syncTheme() {
    const isDark =
      document.documentElement.getAttribute("data-theme") === "dark" ||
      document.body.getAttribute("data-theme") === "dark" ||
      document.documentElement.classList.contains("dark") ||
      document.body.classList.contains("dark");
    this.toggleAttribute("data-theme", isDark);
  }

  private observeThemeChanges() {
    this.themeObserver = new MutationObserver(() => {
      this.syncTheme();
      this.requestUpdate();
    });
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme", "class"],
    });
    this.themeObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ["data-theme", "class"],
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.creditoUpdateTimer) {
      clearTimeout(this.creditoUpdateTimer);
      this.creditoUpdateTimer = null;
    }
    this.clearRefreshInterval();
    this.removeEventListeners();
    this.themeObserver?.disconnect();
  }

  // Aplica datos del singleton al estado local de esta instancia
  private applySharedData(credito: number | null, updateTime: Date | null, error: string | null) {
    if (error) {
      this.hasError = true;
      this.errorMessage = error;
    } else {
      this.hasError = false;
      this.errorMessage = "";
      this.creditoDisponible = credito;
      this.lastKnownCredito = credito;
      this.lastUpdateTime = updateTime;
      this.manualRetryCount = 0;
    }
    this.isLoading = false;
  }

  private handleSharedDataUpdated = (e: Event) => {
    const { credito, updateTime, error } = (e as CustomEvent).detail;
    this.applySharedData(credito, updateTime, error);
  };

  private setupEventListeners() {
    // Solo refrescar crédito en eventos que realmente lo modifican (no en cart-updated genérico)
    window.addEventListener("product-added-to-cart", this.handleCreditUpdate);
    window.addEventListener("product-removed-from-cart", this.handleCreditUpdate);
    window.addEventListener("combo-added-to-cart", this.handleCreditUpdate);
    window.addEventListener("order-sent", this.handleCreditUpdate);
    window.addEventListener("refresh-credit", this.handleCreditUpdate);
    window.addEventListener("credito-data-updated", this.handleSharedDataUpdated);
  }

  private removeEventListeners() {
    if (this.creditoUpdateTimer) {
      clearTimeout(this.creditoUpdateTimer);
      this.creditoUpdateTimer = null;
    }
    window.removeEventListener("product-added-to-cart", this.handleCreditUpdate);
    window.removeEventListener("product-removed-from-cart", this.handleCreditUpdate);
    window.removeEventListener("combo-added-to-cart", this.handleCreditUpdate);
    window.removeEventListener("order-sent", this.handleCreditUpdate);
    window.removeEventListener("refresh-credit", this.handleCreditUpdate);
    window.removeEventListener("credito-data-updated", this.handleSharedDataUpdated);
  }

  private handleCreditUpdate = () => {
    // Si el crédito fue cargado hace menos de CREDITO_COOLDOWN_MS, ignorar el evento
    if (_sharedUpdateTime) {
      const elapsed = Date.now() - _sharedUpdateTime.getTime();
      if (elapsed < this.CREDITO_COOLDOWN_MS) return;
    }
    if (this.creditoUpdateTimer) {
      clearTimeout(this.creditoUpdateTimer);
    }
    this.creditoUpdateTimer = window.setTimeout(() => {
      this.loadCredito(false);
      this.creditoUpdateTimer = null;
    }, this.CREDITO_DEBOUNCE_DELAY);
  };

  public refresh(): void {
    this.loadCredito(false);
  }

  private setupRefreshInterval() {
    // Refrescar cada 2 minutos
    this.refreshInterval = window.setInterval(() => {
      this.loadCredito();
    }, 120000);
  }

  private clearRefreshInterval() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }

  private async loadCredito(isManualRetry = false) {
    const userType = localStorage.getItem("user_type");
    const role = localStorage.getItem("role");
    const userData = localStorage.getItem("user");
    const isLoginPage = window.location.hash.includes("/login") || window.location.pathname.includes("/login");
    const isAdminView = window.location.hash.includes("/admin") || window.location.pathname.includes("/admin");

    if (isAdminView || isLoginPage || userType === "admin" || role === "admin" || !userData) {
      this.isLoading = false;
      return;
    }

    // Si ya hay un fetch en curso (otra instancia), esta instancia espera el broadcast
    if (_fetchLockActive) return;
    _fetchLockActive = true;

    this.isLoading = true;
    this.hasError = false;
    this.errorMessage = "";

    try {
      const response = (await ApiService.get("cuenta/credito")) as any;

      if (response?.success && response.data) {
        const data = response.data;
        this.creditoDisponible = data.disponible ?? data.credito_disponible ?? 0;
        this.lastKnownCredito = this.creditoDisponible;
        this.lastUpdateTime = new Date();
        this.manualRetryCount = 0;

        // Actualizar singleton y notificar a las otras instancias
        _sharedCredito = this.creditoDisponible;
        _sharedUpdateTime = this.lastUpdateTime;
        _sharedError = null;
        broadcastCreditoData();
      } else {
        throw new Error(response?.message || "Error al obtener crédito");
      }
    } catch (error) {
      console.error("Error al cargar crédito:", error);
      this.hasError = true;

      // Determinar tipo de error y mensaje apropiado
      if (error instanceof TypeError && error.message.includes("fetch")) {
        this.errorMessage = "Sin conexión";
      } else if (error instanceof Error && error.message.includes("500")) {
        this.errorMessage = "Error del servidor";
      } else if (error instanceof Error && error.message.includes("404")) {
        this.errorMessage = "Servicio no disponible";
      } else {
        this.errorMessage = "Error al cargar";
      }

      // Si es reintento manual, incrementar contador
      if (isManualRetry) {
        this.manualRetryCount++;
      }

      // Mostrar alerta después de 3 reintentos manuales fallidos
      if (this.manualRetryCount >= this.MAX_MANUAL_RETRIES) {
        import("sweetalert2").then(({ default: Swal }) => {
          Swal.fire({
            icon: "warning",
            title: "Servicio temporalmente no disponible",
            html: `
              <p style="margin-bottom: 1rem;">No se pudo conectar con el servicio de créditos después de varios intentos.</p>
              <p style="font-weight: 600; color: #059669; margin-bottom: 1rem;">
                ✓ Se notificó el error al departamento de sistemas
              </p>
              <p style="font-size: 0.9em; color: #666;">
                El servicio estará disponible pronto. El sistema seguirá intentando automáticamente cada 2 minutos.
              </p>
              <p style="font-size: 0.85em; color: #999; margin-top: 1.5rem; font-style: italic;">
                Mientras tanto, puede continuar navegando normalmente.
              </p>
            `,
            confirmButtonText: "Entendido",
            confirmButtonColor: "#3b82f6",
          });
        });
      }

      // Broadcast del error para que la otra instancia también lo refleje
      _sharedError = this.errorMessage;
      _sharedCredito = null;
      broadcastCreditoData();
    } finally {
      this.isLoading = false;
      _fetchLockActive = false; // Liberar lock para que el próximo trigger pueda hacer fetch
    }
  }

  private formatCurrency(amount: number | null): string {
    if (amount === null) return "$0.00";
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  private getCreditClass(credito: number | null): string {
    if (credito === null) return "";
    if (credito > 10000) return "positive";
    if (credito > 0) return "warning";
    return "negative";
  }

  private getTimeAgo(date: Date | null): string {
    if (!date) return "";

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);

    if (diffMinutes < 1) return "Hace un momento";
    if (diffMinutes === 1) return "Hace 1 minuto";
    if (diffMinutes < 60) return `Hace ${diffMinutes} minutos`;

    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours === 1) return "Hace 1 hora";
    if (diffHours < 24) return `Hace ${diffHours} horas`;

    return date.toLocaleString("es-AR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    });
  }

  private handleClick() {
    // Si hay error, es un reintento manual
    if (this.hasError) {
      this.loadCredito(true);
    } else {
      // Si no hay error, solo refrescar (no cuenta como reintento)
      this.loadCredito(false);
    }
  }

  render() {
    const creditClass = this.getCreditClass(this.creditoDisponible);
    const displayValue = this.creditoDisponible ?? this.lastKnownCredito;
    const isStale = this.hasError && this.lastKnownCredito !== null;

    // Título dinámico según el estado
    let titleText = "Click para actualizar";
    if (this.hasError) {
      titleText = this.manualRetryCount >= this.MAX_MANUAL_RETRIES
        ? "Error persistente - Contacte a sistemas"
        : `Click para reintentar (${this.manualRetryCount}/${this.MAX_MANUAL_RETRIES})`;
    }

    // Usar colores basados en luz/oscuro del documento
    const isDark = document.documentElement.getAttribute('data-theme') === 'dark';

    // En tema oscuro: texto claro. En tema claro: texto oscuro
    const labelColor = isDark ? '#ffffff' : '#0f2d59';
    const errorColor = isDark ? '#ff6b7a' : '#ef4444';
    const updateColor = isDark ? '#b8bcc7' : '#9ca3af';
    const hintColor = isDark ? '#d1d5db' : '#6b7280';
    const containerBg = 'transparent';
    const containerBgHover = isDark ? 'rgba(16, 185, 129, 0.1)' : 'transparent';

    const amountColor =
      creditClass === 'positive' ? '#10b981' :
        creditClass === 'warning' ? '#f59e0b' :
          isDark ? '#ff6b7a' : '#ef4444';

    return html`
      <style>
        .credito-container {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 0.15rem;
          padding: 0.5rem 0.85rem;
          background: ${containerBg};
          border: none;
          border-radius: 12px;
          cursor: pointer;
          user-select: none;
          min-width: 110px;
          transition: background 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .credito-container:hover {
          background: ${containerBgHover};
        }
      </style>
      <div class="credito-container" @click=${this.handleClick} title=${titleText}>

        <div style="display: flex; flex-direction: column; gap: 0.15rem; width: 100%; min-width: 0;">
          ${this.hasError
        ? html`
                <div style="display: flex; align-items: baseline; gap: 0.35rem; width: 100%;">
                  <div style="font-size: 0.65rem; font-weight: 600; color: ${labelColor}; text-transform: uppercase; letter-spacing: 0.03em; line-height: 1; white-space: nowrap;">Crédito:</div>
                  <div style="font-size: 0.65rem; color: ${errorColor}; line-height: 1.2;">${this.errorMessage}</div>
                </div>
                ${isStale
            ? html`
                      <div style="font-size: 0.58rem; color: ${updateColor}; font-style: italic; line-height: 1; opacity: 0.8;">
                        ${this.formatCurrency(displayValue)} - Ult. actualización ${this.getTimeAgo(this.lastUpdateTime)}
                      </div>
                    `
            : ""}
                ${this.manualRetryCount > 0 && this.manualRetryCount < this.MAX_MANUAL_RETRIES
            ? html`<div style="font-size: 0.6rem; color: ${hintColor}; font-style: italic; margin-top: 0.1rem;">Reintento ${this.manualRetryCount}/${this.MAX_MANUAL_RETRIES}</div>`
            : ""}
              `
        : html`
                <div style="display: flex; align-items: baseline; gap: 0.35rem; width: 100%;">
                  <div style="font-size: 0.65rem; font-weight: 600; color: ${labelColor}; text-transform: uppercase; letter-spacing: 0.03em; line-height: 1; white-space: nowrap;">Crédito:</div>
                  <div style="font-size: 0.9rem; font-weight: 700; color: ${amountColor}; line-height: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                    ${this.formatCurrency(this.creditoDisponible)}
                  </div>
                </div>
                ${this.lastUpdateTime
            ? html`
                      <div style="font-size: 0.58rem; color: ${updateColor}; font-style: italic; line-height: 1; opacity: 0.8;">
                        Actualizado ${this.getTimeAgo(this.lastUpdateTime)}
                      </div>
                    `
            : ""}
              `}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "credito-header": CreditoHeader;
  }
}
